import mapboxgl from 'mapbox-gl';
import { Injectable } from '@angular/core';

import { YoshiMapStyle } from '../../theme/map-style';

// expose custom interface to enforce a few optional properties and encapsulate google.maps internals
export interface YoshiMarkerOptions extends google.maps.MarkerOptions {
  map: google.maps.Map;
  icon: string | google.maps.Icon | google.maps.Symbol;
}

export interface YoshiMBMarkerOptions {
  map: any;
  iconUrl: string;
  height: number;
  width: number;
  coords: any;
  timestamp?: Date;
}

export interface MapServiceOptions {
  style: MapStyle;
  zoom: number;
  center: any;
}

export enum MapStyle {
  None = '',
  RoadmapWithNoData = 'mapbox://styles/startyoshi/cl12ofuhh005v14rdvico8ime',
  SatelliteWithData = 'mapbox://styles/startyoshi/cl12htxpv000114lgb32nyf4t',
}
const MAP_BOX_KEY =
  'pk.eyJ1Ijoic3RhcnR5b3NoaSIsImEiOiJjbDEyaGw5aDEwMDVwM2NxZXVkYzluMmJvIn0.hIzcvUSp9Mah2FGdmXt-NQ';
const REMOTE_FEATURE_LAYER = 'site-map-polygons';

export const LOCAL_FEATURE_LAYER = 'local-features';

@Injectable()
export class MapProvider {
  private readonly defaultMapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    styles: YoshiMapStyle,
  };

  constructor() {
    mapboxgl.accessToken = MAP_BOX_KEY;
  }

  createMap(
    elementId: string,
    configuration: google.maps.MapOptions
  ): google.maps.Map {
    const ele = document.getElementById(elementId);
    if (ele) {
      return new google.maps.Map(ele, {
        ...configuration,
        ...this.defaultMapOptions,
      });
    }
  }

  createMBMap(container: HTMLElement | string, options: MapServiceOptions) {
    return new mapboxgl.Map({
      container,
      style: options.style,
      center: options.center,
      zoom: options.zoom,
    });
  }

  getFeatures(map: any) {
    const layers = [];
    if (map.getLayer(REMOTE_FEATURE_LAYER)) {
      layers.push(REMOTE_FEATURE_LAYER);
    }
    if (map.getLayer(LOCAL_FEATURE_LAYER)) {
      layers.push(LOCAL_FEATURE_LAYER);
    }
    const mapRef = map.getContainer();
    return map.queryRenderedFeatures(
      [mapRef.offsetWidth / 2, mapRef.offsetHeight / 2],
      {
        layers,
      }
    );
  }

  drawMarker(markerConfig: YoshiMarkerOptions): google.maps.Marker {
    return new google.maps.Marker({
      position: markerConfig.position,
      icon: markerConfig.icon,
      map: markerConfig.map,
    });
  }

  drawMPMarker(config: YoshiMBMarkerOptions) {
    const markerElement = document.createElement('div');
    markerElement.className = 'marker';
    markerElement.style.height = `${config.height}px`;
    markerElement.style.width = `${config.width}px`;
    markerElement.style.backgroundImage = `url(${config.iconUrl})`;
    markerElement.style.backgroundSize = `contain`;

    if (config.timestamp) {
      this.addTimeStamp(markerElement, config.timestamp);
    }
    return new mapboxgl.Marker(markerElement)
      .setLngLat(config.coords)
      .addTo(config.map);
  }

  private addTimeStamp(element: HTMLElement, date: Date) {
    const timestampContainer = document.createElement('div');
    timestampContainer.style.display = 'block';
    element.appendChild(timestampContainer);
    const timestampElement = this.createTimestampElement(this.timeSince(date));
    timestampContainer.appendChild(timestampElement);
  }

  private createTimestampElement(innerText: string) {
    const timestamp = document.createElement('div');
    timestamp.innerText = innerText;
    timestamp.style.height = '26px';
    timestamp.style.width = '26px';
    timestamp.style.lineHeight = '24px';
    timestamp.style.background = 'white';
    timestamp.style.border = '1px solid grey';
    timestamp.style.borderRadius = '13px';
    timestamp.style.position = 'absolute';
    timestamp.style.fontSize = '9px';
    timestamp.style.fontWeight = 'bolder';
    timestamp.style.textAlign = 'center';
    timestamp.style.color = '#606060';
    timestamp.style.left = '25px';
    timestamp.style.top = '-11px';
    return timestamp;
  }
  /**
   * Helper to provide timestamp formatting (example 1 minute in the past -> "1m")
   * Note: this is not currently available via moment.js
   * @param  {Date} date
   */
  private timeSince(date: Date) {
    var seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    var interval = Math.floor(seconds / 31536000);
    if (interval >= 1) {
      return interval + 'y';
    }
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
      return interval + 'M';
    }
    interval = Math.floor(seconds / 86400);
    if (interval >= 1) {
      return interval + 'd';
    }
    interval = Math.floor(seconds / 3600);
    if (interval >= 1) {
      return interval + 'h';
    }
    interval = Math.floor(seconds / 60);
    if (interval >= 1) {
      return interval + 'm';
    }
    return Math.floor(seconds) + 's';
  }

  removeMarker(marker: google.maps.Marker) {
    marker.setMap(null);
  }

  fitMapBounds(map: google.maps.Map, bounds: google.maps.LatLngBounds) {
    map.fitBounds(bounds);
  }

  setMapTypeId(map: google.maps.Map, type: google.maps.MapTypeId) {
    map.setMapTypeId(type);
  }
}



// WEBPACK FOOTER //
// ./src/providers/map/map.ts